import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListChecks } from "lucide-react";
import {
  buyerSubmissionsApi,
  buyersApi,
  type BuyerBulkPresentResultDto,
  type BuyerListingAssociationRowDto,
} from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import { useRecordDrawerParam } from "@/components/layout/EntityRecordDrawer";
import {
  Button,
  EmptyState,
  Input,
  OverflowMenu,
  Skeleton,
  SubTable,
  Surface,
  TableValue,
  Tooltip,
  TruncateValue,
  useToast,
  type OverflowMenuItem,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import {
  listingNameOrFallback,
  listingShortNameOrFallback,
  resolvedShortListingName,
} from "@/lib/listingDisplay";
// Modal / ConfirmModal are staff-shell components, not part of the portal kit.
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { matchesListingAgentSearch } from "@/lib/listingAgentSearch";
import { listingAgentColumns } from "../listingAgentColumn";
import { listingMatchKeys } from "../presentBuyer/listingMatchKeys";
import { buyerKeys, buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { PasteToCreateListingModal } from "../PasteToCreateListingModal";
import { InlineQueryError } from "../queryStates";
import { ListingRecordDrawer, SubmissionRecordDrawer } from "../recordDrawers";
import type { TabKey as SubmissionTabKey } from "../submission/submissionTabs";
import {
  AssociationStatusBadge,
  PresentControlLabel,
  PresentedBadge,
  associationStatusText,
  locationOf,
} from "./associationBits";
import { associationsCopy as copy } from "./associationsCopy";
import {
  isPresented,
  useAssociationSelection,
  type AssociationSelection,
} from "./associationSelection";
import { BulkPresentResult, PresentSelectedBar } from "./PresentSelectedBar";
import { TieExistingListingModal } from "./TieExistingListingModal";

/** Stable stand-in while the list has not arrived, so the selection's prune
 *  effect is not re-run by a fresh `[]` every render. */
const NO_ROWS: BuyerListingAssociationRowDto[] = [];

/**
 * Everything this buyer is looking at.
 *
 * <p><b>The list IS the point.</b> Every fact a specialist needs to decide what
 * to work next — the business, what it costs, where it is, what industry, and
 * how far along it is — is on the row without a click. A surface that made any
 * of those a drill-in would be a list of links rather than a working queue.</p>
 *
 * <p>An association is a SHORTLIST entry, not a submission: it records that the
 * buyer is considering a listing. Presenting it is the separate, deliberate act
 * that creates the submission the badge is then derived from — which is why an
 * untouched row reads "Not yet worked" rather than borrowing the first funnel
 * status.</p>
 *
 * <p><b>Rows OPEN, they do not navigate.</b> A row writes <code>?listing=</code>
 * (or <code>?submission=</code>) and the record's own body renders in a drawer
 * over this buyer — so looking at a listing costs nothing: the buyer's tab, this
 * list and its scroll position are all still there when the drawer closes. The
 * parameter makes the open record a shareable, refresh-surviving address, and
 * the drawer's "Open full page" is the way to the record's own route when a
 * reader wants to stay there. Every one of these used to be a
 * <code>&lt;Link&gt;</code> that threw the workspace away.</p>
 */
export function AssociatedListingsTab({
  buyerLeadId,
  canManage,
  onPresentListing,
}: {
  buyerLeadId: string;
  /**
   * Whether this user may change the buyer's list. Fed by the buyer's
   * SERVER-RESOLVED `canEdit` — the same flag every other write affordance on
   * the detail page uses — so the ownership rule is never re-derived here.
   */
  canManage: boolean;
  /**
   * Hands the guided wizard this buyer AND this listing, so a shortlist row is
   * presented without searching for either half again.
   *
   * <p>Absent when the reader may not present — the row action is then not
   * offered at all rather than deep-linking somewhere they will be refused.</p>
   */
  onPresentListing?: (row: BuyerListingAssociationRowDto) => void;
}) {
  const [tying, setTying] = useState(false);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: buyerKeys.associatedListings(buyerLeadId),
    queryFn: () => buyersApi.listAssociatedListings(buyerLeadId),
    enabled: Boolean(buyerLeadId),
  });

  // D13 — the tick column, select-all and the bulk bar exist exactly where
  // PRESENT THIS ONE does: the same flag, so the bulk act is never offered to
  // someone the single act is withheld from.
  const canPresent = Boolean(canManage && onPresentListing);
  const [pageListingIds, setPageListingIds] = useState<readonly string[]>([]);
  const onPageRowsChange = useCallback(
    (shown: BuyerListingAssociationRowDto[]) => setPageListingIds(shown.map((r) => r.listingId)),
    [],
  );
  const [confirmingBulk, setConfirmingBulk] = useState(false);
  const selection = useAssociationSelection({
    buyerLeadId,
    rows: list.data ?? NO_ROWS,
    pageListingIds,
    hold: confirmingBulk,
  });
  const bulk = useBulkPresent(buyerLeadId, selection);

  // Two independent drawers, two independent parameters — a row's listing and
  // the submission made against it are different records, and a reader can go
  // from one to the other without the second stealing the first's slot.
  const listingDrawer = useRecordDrawerParam("listing");
  const submissionDrawer = useRecordDrawerParam<SubmissionTabKey>("submission");

  // Titles come from the row that is already on screen, so the drawer is titled
  // the moment it opens. A parameter for a record this buyer has no row for —
  // a hand-edited or stale URL — degrades to the generic noun rather than
  // blocking the open; the body reports what it actually finds.
  const known = list.data ?? [];
  const openListingRow = known.find((r) => r.listingId === listingDrawer.openId);
  const openSubmissionRow = known.find((r) => r.submissionId === submissionDrawer.openId);

  // Rendered in EVERY branch below, including the loading one: a refresh on a
  // URL that names a record must reopen it while the list behind it is still
  // arriving, not after.
  //
  // At most ONE, deliberately. Through the UI the situation cannot arise — an
  // open drawer covers the rows, and the buyer page drops both parameters on a
  // tab change — so both keys present at once means a hand-edited URL. Two
  // stacked overlays is the wrong answer to that; the listing wins because this
  // tab is the listing shortlist, and the submission is reachable from its row.
  const drawers = listingDrawer.openId ? (
    <ListingRecordDrawer
      key={listingDrawer.openId}
      listingId={listingDrawer.openId}
      // SHORT. The drawer heading is a `text-h3` that wraps rather than
      // truncating, so a marketplace title's pitch ran this card to about ten
      // lines before the record itself started.
      title={resolvedShortListingName(openListingRow?.listingDisplayName, openListingRow?.listingBusinessName) ?? "Listing"}
      subtitle={(openListingRow ? locationOf(openListingRow) : null) ?? undefined}
      onClose={listingDrawer.close}
    />
  ) : submissionDrawer.openId ? (
    <SubmissionRecordDrawer
      key={submissionDrawer.openId}
      submissionId={submissionDrawer.openId}
      title={resolvedShortListingName(openSubmissionRow?.listingDisplayName, openSubmissionRow?.listingBusinessName) ?? undefined}
      subtitle="Submission"
      // R7: "Draft intro email" opens on Buyer intro; every other open on
      // Overview. Local state, never the URL.
      initialTab={submissionDrawer.initialTab}
      onClose={submissionDrawer.close}
    />
  ) : null;

  const header = (
    <div className="flex flex-wrap items-center justify-between gap-space-2 mb-space-3">
      <p className="text-small text-gray-mid m-0">
        Listings this buyer is looking at. Tying one here does not present it.
      </p>
      {canManage && (
        <div className="flex flex-wrap items-center gap-space-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setTying(true)}
            data-testid="associations-tie-existing"
          >
            Tie an existing listing
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setCreating(true)}
            data-testid="associations-create-listing"
          >
            Create a listing
          </Button>
        </div>
      )}
    </div>
  );

  const modals = canManage ? (
    <>
      <TieExistingListingModal
        buyerLeadId={buyerLeadId}
        open={tying}
        onClose={() => setTying(false)}
      />
      {/* Reuses the existing paste-to-create flow verbatim and ties the result,
          so there is exactly one way to create a listing in the product. */}
      <CreateAndTieListing
        buyerLeadId={buyerLeadId}
        open={creating}
        onClose={() => setCreating(false)}
      />
    </>
  ) : null;

  if (list.isLoading) {
    return (
      <div className="grid gap-space-3" data-testid="associations-loading">
        {header}
        <Skeleton className="h-40 w-full" />
        {drawers}
      </div>
    );
  }

  // An unreadable list is NOT an empty list. Saying "nothing here yet" when the
  // request failed invites a specialist to re-tie listings they already have.
  //
  // Routed through the shared `InlineQueryError` rather than the hand-rolled box
  // this shipped with — that box predated `queryStates.tsx` by a commit, and two
  // error-with-retry treatments in one module is how the sentence "the request
  // failed, so nothing here is a statement about what is on file" ends up said
  // in one place and not the other.
  if (list.isError || !list.data) {
    return (
      <div data-testid="associations-error">
        {header}
        <InlineQueryError
          what="this buyer's listings"
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
        {modals}
        {drawers}
      </div>
    );
  }

  const rows = list.data;

  /**
   * 🔴 THE ROWS STAY RICH. This is a working shortlist, not a summary: the
   * identity cell carries the clamped note that says WHY the listing is on the
   * buyer's list, and the last cell carries the row's own actions. Adopting
   * shared chrome governs how a table looks and when it grows controls, not how
   * much a row is allowed to say.
   *
   * `searchText` is supplied wherever the rendered cell is a component rather
   * than a string — `SubTable` falls back to the cell's text, and a status
   * badge's text is not what a reader types when they are looking for a
   * listing. The note is deliberately searchable: it is the only place a
   * specialist's own words about a listing live.
   */
  const columns: SubTableColumn<BuyerListingAssociationRowDto>[] = [
    ...(canPresent ? [selectColumn(selection)] : []),
    {
      key: "listing",
      header: "Listing",
      searchText: (row) =>
        [listingNameOrFallback(row.listingDisplayName, row.listingBusinessName), row.note]
          .filter(Boolean)
          .join(" "),
      render: (row) => (
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-space-2">
            {/* SHORT in the cell, whole on hover AND on keyboard focus — the
                pitch is not reachable anywhere else on this tab, and the old
                `title` never opened on focus, so for a keyboard reader it was
                not reachable at all. */}
            <Tooltip label={listingNameOrFallback(row.listingDisplayName, row.listingBusinessName)}>
              <button
                type="button"
                className="min-w-0 max-w-full truncate text-left text-gold-shadow hover:underline"
                onClick={() => listingDrawer.open(row.listingId)}
                data-testid="association-open-listing-name"
              >
                {listingShortNameOrFallback(row.listingDisplayName, row.listingBusinessName)}
              </button>
            </Tooltip>
            {isPresented(row) && <PresentedBadge />}
          </div>
          {/* Bounded in BOTH directions. The note is free text with a
              1,000-character server cap and no client one, so a single pasted
              paragraph could push the Location, Industry, Asking and Status
              columns off the row it belongs to and stretch the table wider
              than its own container. */}
          {row.note && (
            <TruncateValue className="text-caption text-gray-mid mt-space-1 max-w-[28rem]">
              {row.note}
            </TruncateValue>
          )}
        </div>
      ),
    },
    // Addendum B (B1): no COLUMNS picker here, so the agent's phone and email
    // ride a second line under the name.
    ...listingAgentColumns<BuyerListingAssociationRowDto>((row) => row.listingAgent, { withContactLine: true }),
    { key: "location", header: "Location", render: (row) => <TableValue value={locationOf(row)} /> },
    {
      key: "industry",
      header: "Industry",
      render: (row) => <TableValue value={row.listingIndustry} />,
    },
    {
      key: "asking",
      header: "Asking",
      align: "right",
      sortAccessor: (row) => row.listingAskingPrice,
      render: (row) => (
        <TableValue
          value={row.listingAskingPrice == null ? null : formatCurrency(row.listingAskingPrice)}
        />
      ),
    },
    {
      key: "status",
      header: "Status",
      searchText: (row) => associationStatusText(row),
      render: (row) => <AssociationStatusBadge row={row} />,
    },
    {
      key: "actions",
      header: "Actions",
      // Not searchable: a menu is not a value, and letting it contribute would
      // match every row on the word every menu happens to contain.
      searchText: () => "",
      render: (row) => (
        <RowActions
          row={row}
          canManage={canManage}
          onPresent={onPresentListing ? () => onPresentListing(row) : undefined}
          onOpenListing={() => listingDrawer.open(row.listingId)}
          onOpenSubmission={submissionDrawer.open}
        />
      ),
    },
  ];

  if (rows.length === 0) {
    return (
      <div data-testid="associations-empty">
        {header}
        <EmptyState
          icon={ListChecks}
          title="Nothing on this buyer's list yet"
          // Branched, so a read-only viewer is never told to press a button they
          // do not have.
          description={
            canManage
              ? "Tie a listing they are looking at, or paste one to add it to the catalog."
              : "Their consultant has not tied any listings yet."
          }
          action={
            canManage ? { label: "Tie an existing listing", onClick: () => setTying(true) } : undefined
          }
        />
        {modals}
        {drawers}
      </div>
    );
  }

  return (
    <div data-testid="associations-list">
      {header}
      {canPresent && (bulk.result || selection.count > 0) && (
        <div className="grid gap-space-2 mb-space-3">
          {bulk.result && (
            <BulkPresentResult result={bulk.result} onDismiss={bulk.dismissResult} />
          )}
          <PresentSelectedBar
            selection={selection}
            onPresent={() => setConfirmingBulk(true)}
            pending={bulk.mutation.isPending}
          />
        </div>
      )}
      {/* `SubTable` resolves its own header variant from the surface it finds
          itself in, and this tab renders BOTH as a page body and inside a
          buyer drawer. `Surface` declares the card explicitly because
          `DetailSection` publishes nothing, and it is declared here rather
          than pinned with the `surface` escape hatch, which would survive a
          move into a drawer and be wrong there.

          🔴 CLIENT-SIDE CHROME IS HONEST HERE, and that is a fact about the
          endpoint rather than a preference. `ListForBuyerAsync` returns the
          buyer's WHOLE shortlist in one call — it does not page — so
          `SubTable`'s search, sort and footer act on the complete set, not on
          a slice pretending to be one. That is the whole distinction: client
          chrome over a complete set is truthful; client chrome over a partial
          set is the trap. Paging this server-side is recorded as future work
          rather than done: the sort key folds a C# dictionary after
          materialisation, and a curated shortlist is the table least likely
          to need it. */}
      <Surface kind="card">
        <SubTable<BuyerListingAssociationRowDto>
          caption="Associated listings"
          data-testid="associations-table"
          rows={rows}
          rowKey={(row) => row.id}
          columns={columns}
          searchPlaceholder="Search listing, location, industry or agent (name, email, phone)…"
          // The shared listing-agent rule (Addendum B): word by word across the
          // row and its agent, phones by digits — the server's rule, mirrored.
          matchRow={(row, query) =>
            matchesListingAgentSearch(
              query,
              [
                listingNameOrFallback(row.listingDisplayName, row.listingBusinessName),
                row.note,
                locationOf(row),
                row.listingIndustry,
                row.listingAskingPrice == null ? null : formatCurrency(row.listingAskingPrice),
                associationStatusText(row),
              ],
              row.listingAgent,
            )
          }
          onPageRowsChange={canPresent ? onPageRowsChange : undefined}
        />
      </Surface>
      {modals}
      {canPresent && (
        <ConfirmModal
          open={confirmingBulk}
          title={copy.confirmTitle(selection.eligible.length)}
          message={<BulkConfirmMessage selection={selection} />}
          confirmLabel={copy.confirmLabel}
          onConfirm={() => {
            const listingIds = selection.eligible.map((e) => e.listingId);
            setConfirmingBulk(false);
            if (listingIds.length > 0) bulk.mutation.mutate(listingIds);
          }}
          onCancel={() => setConfirmingBulk(false)}
        />
      )}
      {drawers}
    </div>
  );
}

/**
 * D13 — the tick column. Only a not-yet-worked row can be ticked; a presented
 * one shows a DISABLED box carrying the reason in its accessible name and on
 * hover (`pointer-events-none` lets the wrapper receive the hover — a disabled
 * control swallows mouse events in some engines). The header box acts on the
 * selectable rows of the CURRENT PAGE only, which `SubTable` reports.
 */
function selectColumn(
  selection: AssociationSelection,
): SubTableColumn<BuyerListingAssociationRowDto> {
  const { page } = selection;
  return {
    key: "select",
    header: (
      <input
        type="checkbox"
        checked={page.all}
        ref={(el) => {
          if (el) el.indeterminate = page.some && !page.all;
        }}
        onChange={page.toggleAll}
        disabled={page.selectable === 0}
        aria-label={copy.selectAllOnPage}
        data-testid="associations-select-all"
      />
    ),
    searchText: () => "",
    render: (row) => {
      const name = listingShortNameOrFallback(row.listingDisplayName, row.listingBusinessName);
      if (isPresented(row)) {
        return (
          <Tooltip label={copy.alreadyPresented}>
            <span className="inline-flex">
              <input
                type="checkbox"
                disabled
                checked={false}
                readOnly
                className="pointer-events-none"
                aria-label={copy.presentedAriaLabel(name)}
                data-testid={`association-select-${row.listingId}`}
              />
            </span>
          </Tooltip>
        );
      }
      return (
        <input
          type="checkbox"
          checked={selection.isTicked(row.listingId)}
          onChange={() => selection.toggle(row)}
          aria-label={copy.selectAriaLabel(name)}
          data-testid={`association-select-${row.listingId}`}
        />
      );
    },
  };
}

/** D14 — the confirm names every listing it will present, and every tick it
 *  is leaving out and why. */
function BulkConfirmMessage({ selection }: { selection: AssociationSelection }) {
  return (
    <div className="grid gap-space-2" data-testid="associations-present-confirm">
      <p className="m-0">{copy.confirmIntro}</p>
      <ul className="m-0 list-disc pl-space-4" data-testid="associations-present-confirm-list">
        {selection.eligible.map((e) => (
          <li key={e.listingId}>{e.name}</li>
        ))}
      </ul>
      {selection.leftOut.length > 0 && (
        <div data-testid="associations-present-confirm-left-out">
          <p className="m-0 font-bold">{copy.confirmSkippedHeading}</p>
          <ul className="m-0 list-disc pl-space-4">
            {selection.leftOut.map((l) => (
              <li key={l.listingId}>
                {l.name} — {l.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * D14/D15/D16 — ONE request for every ticked listing, then the rows flip.
 *
 * <p>🔴 The cached rows are patched BEFORE the invalidation, so a presented row
 * reads Presented the moment the response lands rather than after the refetch:
 * `submissionId` is the whole of the Presented rule, and the new submission
 * starts Shortlisted. The invalidation then brings the server's own derivation
 * back over the patch. Every submission read (lists, stats, presentations) and
 * the buyer's own record move too — a present moves the buyer to
 * ActivelySubmitting.</p>
 */
function useBulkPresent(buyerLeadId: string, selection: AssociationSelection) {
  const qc = useQueryClient();
  const toast = useToast();
  const [result, setResult] = useState<BuyerBulkPresentResultDto | null>(null);
  const { drop } = selection;

  const mutation = useMutation({
    mutationFn: (listingIds: string[]) =>
      buyerSubmissionsApi.bulkPresent({ buyerLeadId, listingIds }),
    onSuccess: (res) => {
      const created = new Map(
        res.items
          .filter((i) => i.outcome === "Succeeded" && i.submissionId)
          .map((i) => [i.listingId, i.submissionId as string]),
      );
      qc.setQueryData<BuyerListingAssociationRowDto[]>(
        buyerKeys.associatedListings(buyerLeadId),
        (prev) =>
          prev?.map((r) =>
            created.has(r.listingId)
              ? { ...r, submissionId: created.get(r.listingId), submissionStatus: "Shortlisted" }
              : r,
          ),
      );
      drop([...created.keys()]);
      setResult(res);
      const summary = copy.resultSummary(res.presented, res.skipped, res.failed);
      if (res.failed > 0) toast.error(summary);
      else if (res.presented > 0) toast.success(summary);
      else toast.info(summary);
      void qc.invalidateQueries({ queryKey: buyerKeys.associatedListings(buyerLeadId) });
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      // The present wizard's Suggested tier excludes presented listings.
      void qc.invalidateQueries({ queryKey: listingMatchKeys.all });
    },
    onError: (e: unknown) => {
      toast.error(copy.requestFailed(extractErrorMessage(e)));
      // A failed RESPONSE is not proof nothing landed — read the truth back.
      void qc.invalidateQueries({ queryKey: buyerKeys.associatedListings(buyerLeadId) });
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      void qc.invalidateQueries({ queryKey: listingMatchKeys.all });
    },
  });

  return { mutation, result, dismissResult: () => setResult(null) };
}

/**
 * The per-row action set — <b>one primary, everything else behind "⋯".</b>
 *
 * <p>🔴 It was five bare links of equal visual weight, and the one that did
 * something irreversible was the QUIETEST of them: Remove rendered
 * `text-gray-mid` while its four siblings were gold. A destructive action
 * styled as the least important thing in the row is a misread waiting to
 * happen, and five same-weight links say nothing about which one a specialist
 * is meant to reach for.</p>
 *
 * <p>The primary is what the row is FOR: presenting this listing to the buyer
 * whose list it is on. Where that is not offered — a reader who may not present
 * — opening the listing takes its place, so the row is never left with a "⋯"
 * and nothing beside it.</p>
 *
 * <p>"Draft intro email" appears only where a submission exists, because the
 * intro email is written against the negotiation record — offering it on a row
 * with nothing to open would be a button that cannot work. It opens the
 * submission on its Buyer intro tab (R7), where that email is drafted.</p>
 */
function RowActions({
  row,
  canManage,
  onPresent,
  onOpenListing,
  onOpenSubmission,
}: {
  row: BuyerListingAssociationRowDto;
  canManage: boolean;
  /** Absent when the host page cannot present — see `onPresentListing`. */
  onPresent?: () => void;
  onOpenListing: () => void;
  onOpenSubmission: (submissionId: string, initialTab?: SubmissionTabKey) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);

  const canPresent = Boolean(canManage && onPresent);

  const items: OverflowMenuItem[] = [];
  if (canPresent) {
    items.push({ label: "Open listing", onSelect: onOpenListing });
  }
  if (row.submissionId) {
    items.push({
      label: "Draft intro email",
      onSelect: () => onOpenSubmission(row.submissionId!, "intro-email"),
    });
  }
  if (canManage) {
    items.push({
      label: row.note ? "Edit note" : "Add note",
      onSelect: () => setEditing(true),
    });
    items.push({
      label: "Remove from this list",
      destructive: true,
      // 🔴 Deliberately NOT the menu's inline `confirm`. "Remove" reads like a
      // delete, and what actually happens — the listing stays in the shared
      // catalog, and any submission already made to it is untouched — needs a
      // sentence, not a one-line prompt. The tone is what the menu contributes.
      onSelect: () => setRemoving(true),
    });
  }

  return (
    <div className="flex flex-wrap items-center gap-space-2">
      {canPresent && isPresented(row) ? (
        // R3/D16 — a presented row keeps a control in PRESENT THIS ONE's
        // place, disabled and the same width, so the row does not shift and
        // cannot be presented twice. The Research tab's "Attached ✓".
        <Button size="sm" disabled data-testid="association-presented">
          <PresentControlLabel presented />
        </Button>
      ) : canPresent ? (
        <Button size="sm" onClick={onPresent} data-testid="association-present">
          <PresentControlLabel presented={false} />
        </Button>
      ) : (
        <Button
          size="sm"
          variant="secondary"
          onClick={onOpenListing}
          data-testid="association-open-listing"
        >
          Open listing
        </Button>
      )}

      {items.length > 0 && (
        <OverflowMenu
          items={items}
          /* An accessible name a screen reader reads aloud, so the pitch is
             noise here for exactly the reason it is in the cell beside it. */
          label={`More actions for ${listingShortNameOrFallback(
            row.listingDisplayName,
            row.listingBusinessName,
          )}`}
          data-testid="association-row-actions"
        />
      )}

      {canManage && (
        <>
          <EditNoteModal row={row} open={editing} onClose={() => setEditing(false)} />
          <RemoveAssociationModal
            row={row}
            open={removing}
            onClose={() => setRemoving(false)}
          />
        </>
      )}
    </div>
  );
}

/** The server's own cap, mirrored — `BuyerListingAssociationConfiguration`
 *  declares `HasMaxLength(1000)` on `Note`. */
const NOTE_MAX_LENGTH = 1000;

/** The note is the only editable field: the listing an association points at is
 *  the row's identity, so changing that would be a remove plus an add. */
function EditNoteModal({
  row,
  open,
  onClose,
}: {
  row: BuyerListingAssociationRowDto;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState(row.note ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      buyersApi.updateAssociatedListing(row.id, { note: note.trim() || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success("Note saved.");
      onClose();
    },
    onError: (e: unknown) => setError(extractErrorMessage(e)),
  });

  return (
    <Modal
      open={open}
      onClose={() => (save.isPending ? undefined : onClose())}
      title="Note on this listing"
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="text" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            loading={save.isPending}
            data-testid="association-note-save"
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-2">
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why this one is worth a look"
          aria-label="Note"
          /* Matches `BuyerListingAssociationConfiguration`'s HasMaxLength(1000)
             exactly. Without it the only feedback on an over-long note was a
             server error after the save — a limit enforced where it cannot be
             seen is not a limit anyone can work within. */
          maxLength={NOTE_MAX_LENGTH}
          data-testid="association-note-input"
        />
        <p className="text-caption text-gray-mid m-0 text-right">
          {note.length}/{NOTE_MAX_LENGTH}
        </p>
        {error && (
          <p className="text-small text-status-error m-0" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

/** Removing takes the listing off this buyer's list and nothing else — the
 *  listing is a shared catalog record, and any submission already made to it
 *  survives. Said plainly, because "remove" reads like a delete. */
function RemoveAssociationModal({
  row,
  open,
  onClose,
}: {
  row: BuyerListingAssociationRowDto;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const remove = useMutation({
    mutationFn: () => buyersApi.removeAssociatedListing(row.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success("Taken off this buyer's list.");
      onClose();
    },
    onError: (e: unknown) => {
      toast.error(extractErrorMessage(e));
      onClose();
    },
  });

  return (
    <ConfirmModal
      open={open}
      title="Take this off the list?"
      message={
        <>
          {/* A sentence naming a record wants the record's NAME. The pitch mid-
              sentence reads as a run-on, and this is internal copy rather than
              the outbound prose where the pitch is the point. */}
          {resolvedShortListingName(row.listingDisplayName, row.listingBusinessName) ?? "This listing"} comes off this buyer&apos;s
          list. The listing itself stays in the catalog, and any submission
          already made to it is untouched.
        </>
      }
      confirmLabel="Remove"
      destructive
      onConfirm={() => remove.mutate()}
      onCancel={onClose}
    />
  );
}

/** Create a listing, then tie it — the paste-to-create modal reused verbatim,
 *  with the association written on its `onCreated` callback. */
function CreateAndTieListing({
  buyerLeadId,
  open,
  onClose,
}: {
  buyerLeadId: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const tie = useMutation({
    mutationFn: (listingId: string) =>
      buyersApi.associateListing(buyerLeadId, { listingId, note: null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success("Listing created and added to this buyer's list.");
    },
    // The listing itself WAS created — reporting the tie's failure as a failed
    // create would invite a duplicate listing. Say what actually happened.
    onError: (e: unknown) =>
      toast.error(
        `The listing was created, but it could not be tied to this buyer: ${extractErrorMessage(e)}`,
      ),
  });

  return (
    <PasteToCreateListingModal
      open={open}
      onClose={onClose}
      onCreated={(created) => tie.mutate(created.id)}
    />
  );
}
